/**
 * Editorial CMS hooks (V11 Phase 3).
 *
 * `useContent(pageKey)` fetches the aggregated page envelope from
 * `/api/content/pages/{page_key}` and returns:
 *
 *   { content, loading, error, block, field, isPreview }
 *
 * Where:
 *   - `content` is the raw envelope: { page_key, title, seo, blocks: {...} }.
 *   - `block(key)` returns the block's `content` object (or {} if missing).
 *   - `field(blockKey, fieldKey, fallback)` returns the resolved value with
 *     the priority DB > empty-string-preserved > fallback.
 *   - `isPreview` is true when the request served draft content via a valid
 *     ?preview=<jwt> parameter (admin-only preview session).
 *
 * `useUiString(key, fallback)` reads from the global UI-strings dictionary
 * (cached process-wide after the first fetch) and applies the same priority.
 *
 * Both hooks respect SITE_CONFIG.ENABLE_CMS_WIRE — when disabled, the hooks
 * return no data so every consumer falls back to its hardcoded copy.
 *
 * Design principles:
 *   - One HTTP call per page (not per block).
 *   - No global loader spinner — components must render with fallbacks
 *     immediately, then swap to DB values as they arrive.
 *   - Empty string `""` is a valid, INTENTIONAL editorial value that hides
 *     the label. Never coerced to fallback.
 */
import { useEffect, useState } from "react";
import { contentApi } from "./api";
import { SITE_CONFIG } from "../config/site";

// -----------------------------------------------------------------------------
// Process-wide caches
// -----------------------------------------------------------------------------
const _pageCache = new Map();      // page_key -> { data, timestamp, promise }
const _stringsCache = { data: null, timestamp: 0, promise: null };
const PAGE_TTL_MS = 5 * 60 * 1000;  // 5 min
const STRINGS_TTL_MS = 10 * 60 * 1000;

function _getPreviewToken() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("preview");
}

/**
 * useContent(pageKey)
 * Reads the CMS envelope for a page and exposes helpers to resolve fields
 * with fallbacks.
 */
export function useContent(pageKey) {
  const [content, setContent] = useState(_pageCache.get(pageKey)?.data || null);
  const [loading, setLoading] = useState(!content);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!SITE_CONFIG.ENABLE_CMS_WIRE) {
      setLoading(false);
      return;
    }
    const previewToken = _getPreviewToken();
    const cacheKey = previewToken ? `${pageKey}::preview::${previewToken}` : pageKey;
    const cached = _pageCache.get(cacheKey);
    if (cached && !previewToken && Date.now() - cached.timestamp < PAGE_TTL_MS) {
      setContent(cached.data);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const promise = cached?.promise || contentApi.getPage(pageKey, previewToken)
      .then((res) => {
        _pageCache.set(cacheKey, { data: res.data, timestamp: Date.now(), promise: null });
        return res.data;
      })
      .catch((err) => {
        _pageCache.set(cacheKey, { data: null, timestamp: Date.now(), promise: null });
        throw err;
      });
    if (!cached?.promise) _pageCache.set(cacheKey, { ...(cached || {}), promise });

    promise
      .then((data) => { if (!cancelled) { setContent(data); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err); setLoading(false); } });

    return () => { cancelled = true; };
  }, [pageKey]);

  /** Return a block's `content` object (or {} if missing). */
  const block = (blockKey) => content?.blocks?.[blockKey]?.content || {};

  /** Return a field with fallback priority: DB > empty-string-preserved > fallback. */
  const field = (blockKey, fieldKey, fallback = "") => {
    if (!SITE_CONFIG.ENABLE_CMS_WIRE) return fallback;
    const c = content?.blocks?.[blockKey]?.content;
    if (!c) return fallback;
    if (!(fieldKey in c)) return fallback;
    const v = c[fieldKey];
    if (v === null || v === undefined) return fallback;
    // Empty string is INTENTIONAL — preserved (renders nothing).
    return v;
  };

  /** Return a raw block object (for advanced consumers). */
  const rawBlock = (blockKey) => content?.blocks?.[blockKey] || null;

  return {
    content,
    loading,
    error,
    block,
    field,
    rawBlock,
    seo: content?.seo || null,
    isPreview: !!content?.preview,
  };
}

/**
 * useUiString(key, fallback)
 * Reads a single UI string with fallback. Also returns a `strings` map for
 * consumers that need many at once.
 */
export function useUiString(key, fallback = "") {
  const [strings, setStrings] = useState(_stringsCache.data);

  useEffect(() => {
    if (!SITE_CONFIG.ENABLE_CMS_WIRE) return;
    if (_stringsCache.data && Date.now() - _stringsCache.timestamp < STRINGS_TTL_MS) return;
    if (_stringsCache.promise) {
      _stringsCache.promise.then(setStrings).catch(() => {});
      return;
    }
    _stringsCache.promise = contentApi.getUiStrings()
      .then((res) => {
        _stringsCache.data = res.data?.strings || {};
        _stringsCache.timestamp = Date.now();
        _stringsCache.promise = null;
        return _stringsCache.data;
      })
      .catch((err) => { _stringsCache.promise = null; throw err; });
    _stringsCache.promise.then(setStrings).catch(() => {});
  }, []);

  const resolve = (k, fb = "") => {
    if (!SITE_CONFIG.ENABLE_CMS_WIRE) return fb;
    const dict = strings || _stringsCache.data;
    if (!dict) return fb;
    if (!(k in dict)) return fb;
    const v = dict[k];
    // Empty string is INTENTIONAL — preserved.
    return v === undefined || v === null ? fb : v;
  };

  return {
    value: resolve(key, fallback),
    t: resolve,           // alias for accessing many keys inline
    strings: strings || {},
    loaded: !!strings,
  };
}

/** Utility for components that need to renderHeadline with <em> highlights. */
export function renderHighlightedTitle(text, highlightTerms) {
  if (!text) return null;
  const terms = Array.isArray(highlightTerms) ? highlightTerms.filter(Boolean) : [];
  if (terms.length === 0) return text;
  // Escape regex special chars in each term
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(${escaped.join("|")})`, "g");
  const parts = text.split(pattern);
  return parts.map((part, i) => {
    const isHl = terms.some((t) => t === part);
    return isHl ? <em key={i} className="font-normal text-[#12964A] not-italic">{part}</em> : <span key={i}>{part}</span>;
  });
}
